
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Crown, Copy, Users, Settings as SettingsIcon, ArrowLeft, Trash2, LogOut, Edit2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import API from "../config";

export default function Lobby() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerId, setPlayerId] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newName, setNewName] = useState("");


  const fetchRoom = async () => {
    const id = localStorage.getItem(`room_${roomCode}`);
    if (!id) return; // Stop fetching if player has left

    try {
      const response = await axios.get(`${API}/rooms/${roomCode}`, {
        params: { player_id: id }
      });
      setRoom(response.data);
      setError(null);
      if (response.data.status === "playing") {
        navigate(`/game/${roomCode}`);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching room:", error);
      if (error.response?.status === 404) {
        toast.error("La sala ya no existe.");
        localStorage.removeItem(`room_${roomCode}`);
        navigate("/");
      } else {
        setError("Error de conexión. Reintentando...");
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = localStorage.getItem(`room_${roomCode}`);
    if (!id) {
      navigate("/");
      return;
    }
    setPlayerId(id);

    const interval = setInterval(fetchRoom, 2000);
    const activityInterval = setInterval(async () => {
      const currentId = localStorage.getItem(`room_${roomCode}`);
      if (!currentId) return;
      try {
        await axios.post(`${API}/rooms/${roomCode}/update-activity`, null, {
          params: { player_id: currentId }
        });
      } catch (error) {
        // This can happen if the room is deleted, so we don't need to show an error
        console.error("Error updating activity:", error);
      }
    }, 30000);

    fetchRoom(); // Initial fetch

    return () => {
      clearInterval(interval);
      clearInterval(activityInterval);
    };
  }, [roomCode, navigate]);

  useEffect(() => {
    if (room && playerId) {
      setIsHost(room.host_id === playerId);
    }
  }, [room, playerId]);

  const copyRoomCode = () => {
    navigator.clipboard.writeText(roomCode).then(() => toast.success("Código copiado"));
  };

  const handleLeaveLobby = async () => {
    try {
      await axios.post(`${API}/rooms/${roomCode}/leave`, null, {
        params: { player_id: playerId },
      });
      toast.success("Has salido de la sala");
    } catch (error) {
      console.error("No se pudo notificar al servidor la salida:", error);
    } finally {
      localStorage.removeItem(`room_${roomCode}`);
      navigate("/");
    }
  };

  const handleRename = async () => {
    if (!newName.trim()) return;
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/rename`, {
        player_id: playerId,
        new_name: newName
      });
      const actualName = response.data.new_name || newName.trim();
      localStorage.setItem('player_name', actualName);
      toast.success("Nombre actualizado");
      setShowRenameModal(false);
      fetchRoom();
    } catch (error) {
      toast.error("Error al cambiar nombre");
    }
  };

  const deleteRoom = async () => {
    if (!confirm("¿Estás seguro de que quieres eliminar esta sala?")) return;
    try {
      await axios.delete(`${API}/rooms/${roomCode}`, {
        params: { player_id: playerId }
      });
      toast.success("Sala eliminada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar sala");
    } finally {
      localStorage.removeItem(`room_${roomCode}`);
      navigate("/");
    }
  };

  const updateSettings = async (fieldOrObject, value) => {
    if (!isHost) return;

    let updates = {};
    if (typeof fieldOrObject === 'string') {
      updates = { [fieldOrObject]: value };
    } else {
      updates = fieldOrObject;
    }

    setRoom(prev => ({ ...prev, settings: { ...prev.settings, ...updates } }));
    try {
      await axios.post(`${API}/rooms/${roomCode}/settings`, updates, {
        params: { player_id: playerId }
      });
    } catch (error) {
      toast.error("Error al actualizar configuración");
      fetchRoom(); // Re-fetch to get correct state
    }
  };

  const startGame = async () => {
    if (!isHost) return;
    if (!room || room.players.length < 3) {
      toast.error("Se necesitan al menos 3 jugadores");
      return;
    }

    const dist = getRoleDistribution(room.players.length, room.settings);
    if (dist.infiltrators + dist.impostor === 0) {
      toast.error("La partida debe tener al menos un Infiltrado o un Impostor");
      return;
    }
    try {
      await axios.post(`${API}/rooms/${roomCode}/start`, null, {
        params: { player_id: playerId }
      });
      toast.success("¡Partida iniciada!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar");
    }
  };

  const handleTransferHost = async (newHostId) => {
    if (!isHost) return;
    try {
      const response = await axios.post(`${API}/rooms/${roomCode}/transfer-host`,
        { new_host_id: newHostId },
        { params: { player_id: playerId } }
      );
      toast.success(response.data.message || "Liderazgo transferido.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al transferir.");
    }
  };

  const getRoleDistribution = (playerCount, settings = {}) => {
    if (settings.override_roles) {
      const agents = Math.max(0, playerCount - (settings.infiltrators_count || 0) - (settings.impostor_count || 0));
      return { agents, infiltrators: settings.infiltrators_count || 0, impostor: settings.impostor_count || 0 };
    }

    const distributions = {
      3: { agents: 2, infiltrators: 1, impostor: 0 },
      4: { agents: 2, infiltrators: 1, impostor: 1 },
      5: { agents: 3, infiltrators: 1, impostor: 1 },
      6: { agents: 3, infiltrators: 2, impostor: 1 },
      7: { agents: 4, infiltrators: 2, impostor: 1 },
      8: { agents: 4, infiltrators: 3, impostor: 1 },
      9: { agents: 5, infiltrators: 3, impostor: 1 },
      10: { agents: 6, infiltrators: 3, impostor: 1 },
      11: { agents: 7, infiltrators: 3, impostor: 1 },
      12: { agents: 7, infiltrators: 4, impostor: 1 },
      13: { agents: 8, infiltrators: 4, impostor: 1 },
      14: { agents: 8, infiltrators: 5, impostor: 1 },
      15: { agents: 9, infiltrators: 5, impostor: 1 },
      16: { agents: 10, infiltrators: 5, impostor: 1 },
      17: { agents: 10, infiltrators: 6, impostor: 1 },
      18: { agents: 11, infiltrators: 6, impostor: 1 },
      19: { agents: 11, infiltrators: 7, impostor: 1 },
      20: { agents: 12, infiltrators: 7, impostor: 1 },
    };
    return distributions[playerCount] || distributions[20] || distributions[9];
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center scanlines"><div className="font-heading text-2xl text-[hsl(var(--cyber-yellow))] glow-pulse">CARGANDO...</div></div>;
  if (!room) return null;

  const distribution = getRoleDistribution(room.players.length, room.settings);

  return (
    <div className="min-h-screen scanlines p-4 md:p-8" data-testid="lobby-page">
      <div className="glass rounded-lg p-6 mb-8 sticky top-4 z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button onClick={handleLeaveLobby} size="icon" variant="ghost" className="hover:bg-[hsl(var(--charcoal))]" data-testid="back-button" title="Abandonar Sala">
              <LogOut className="w-5 h-5" />
            </Button>
            <div>
              <div className="text-xs text-[hsl(var(--text-muted))] font-heading mb-1">CÓDIGO DE SALA</div>
              <div className="font-heading text-4xl font-bold text-[hsl(var(--cyber-yellow))] tracking-wider" data-testid="room-code-display">
                {roomCode}
              </div>
            </div>
            <Button onClick={copyRoomCode} size="icon" variant="ghost" className="hover:bg-[hsl(var(--charcoal))]"><Copy className="w-5 h-5" /></Button>
            {isHost && (<Button onClick={deleteRoom} size="icon" variant="ghost" className="hover:bg-[hsl(var(--hot-pink))]/20 text-[hsl(var(--hot-pink))]"><Trash2 className="w-5 h-5" /></Button>)}
          </div>

          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-[hsl(var(--electric-cyan))]" />
            <span className="font-heading text-xl" data-testid="player-count">
              {room.players.length} JUGADORES
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 className="font-display text-3xl font-bold mb-6">Jugadores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {room.players.map((player) => (
              <div key={player.id} className="bg-[hsl(var(--deep-slate))] border border-[hsl(var(--smoke))]/40 rounded-lg p-6 hover:-translate-y-1 transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--charcoal))] flex items-center justify-center font-heading text-xl">{player.name[0].toUpperCase()}</div>
                    <div>
                      <div className="font-heading font-semibold text-lg flex items-center gap-2">
                        {player.name}
                        {player.id === playerId && (
                          <button onClick={() => { setNewName(player.name); setShowRenameModal(true); }} className="text-[hsl(var(--text-muted))] hover:text-white transition-colors">
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {player.points > 0 && <div className="text-sm text-[hsl(var(--text-muted))]">{player.points} puntos</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!player.is_connected && <Badge variant="outline" className="text-xs border-amber-500 text-amber-500">OFFLINE</Badge>}
                    {player.is_host ? (
                      <div className="flex items-center gap-2 text-[hsl(var(--cyber-yellow))]">
                        <Crown className="w-5 h-5" />
                        <span className="text-sm font-heading">HOST</span>
                      </div>
                    ) : isHost ? (
                      <Button size="sm" variant="outline" onClick={() => handleTransferHost(player.id)} className="border-[hsl(var(--cyber-yellow))] text-[hsl(var(--cyber-yellow))] hover:bg-[hsl(var(--cyber-yellow))]/10 hover:text-[hsl(var(--cyber-yellow))]">
                        <Crown className="w-4 h-4 mr-2" />Hacer Host
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="glass rounded-lg p-6 space-y-6">
            {error && (
              <div className="bg-[hsl(var(--hot-pink))]/10 border border-[hsl(var(--hot-pink))]/30 rounded-lg p-3 flex items-center gap-2 mb-2 animate-pulse">
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--hot-pink))]" />
                <span className="text-[10px] font-black text-[hsl(var(--hot-pink))] uppercase tracking-widest">{error}</span>
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <SettingsIcon className="w-6 h-6 text-[hsl(var(--electric-cyan))]" />
              <h3 className="font-heading text-xl font-semibold">CONFIGURACIÓN</h3>
            </div>

            {isHost ? (
              <>
                {/* --- SECCIÓN 1: MODO DE JUEGO --- */}
                <div className="space-y-3">
                  <Label className="font-heading text-sm">Modo de Juego</Label>
                  <Select value={room.settings?.game_mode || "infinite"} onValueChange={(v) => updateSettings("game_mode", v)}>
                    <SelectTrigger className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="infinite">Infinito</SelectItem>
                      <SelectItem value="rounds">Rondas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {room.settings?.game_mode === "rounds" && (
                  <div className="space-y-3 mt-3">
                    <Label className="font-heading text-sm">Máximo de Rondas</Label>
                    <Input type="number" min="1" max="10" value={room.settings?.max_rounds || 5} onChange={(e) => updateSettings("max_rounds", parseInt(e.target.value))} className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]" />
                  </div>
                )}

                <div className="border-t border-[hsl(var(--smoke))] pt-2 pb-2 mt-4" />

                {/* --- SECCIÓN 2: MECÁNICAS --- */}
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label className="font-heading text-sm">Tipo de Votación</Label>
                    <Select value={room.settings?.vote_type || "manual"} onValueChange={(v) => updateSettings("vote_type", v)}>
                      <SelectTrigger className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="secret">Secreta</SelectItem>
                        <SelectItem value="public">Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="font-heading text-sm">Revelar Roles al morir</Label>
                    <Switch checked={room.settings?.reveal_roles ?? true} onCheckedChange={(c) => updateSettings("reveal_roles", c)} />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="font-heading text-sm">Asistencia Inicio (Jugador inicial)</Label>
                    <Switch checked={room.settings?.starting_player_assistance ?? true} onCheckedChange={(c) => updateSettings("starting_player_assistance", c)} />
                  </div>

                </div>

                <div className="border-t border-[hsl(var(--smoke))] pt-2 pb-2 mt-4" />

                {/* --- SECCIÓN EXTRA: ROLES PERSONALIZADOS --- */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-heading text-sm text-[hsl(var(--cyber-yellow))]">Personalizar Distribución</Label>
                    <Switch
                      checked={room.settings?.override_roles ?? false}
                      onCheckedChange={(c) => {
                        if (c) {
                          // Initialize with current standard values if enabling
                          const dist = getRoleDistribution(room.players.length);
                          updateSettings({
                            override_roles: true,
                            infiltrators_count: dist.infiltrators,
                            impostor_count: dist.impostor
                          });
                        } else {
                          updateSettings("override_roles", false);
                        }
                      }}
                    />
                  </div>

                  {room.settings.override_roles && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Infiltrados</Label>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateSettings("infiltrators_count", Math.max(0, (room.settings.infiltrators_count || 0) - 1))}
                            className="w-8 h-8 rounded-full bg-[hsl(var(--hot-pink))]/10 text-[hsl(var(--hot-pink))] flex items-center justify-center hover:bg-[hsl(var(--hot-pink))]/20 active:scale-90 transition-all font-bold border border-[hsl(var(--hot-pink))]/20"
                          >-</button>
                          <span className="text-xl font-black text-[hsl(var(--hot-pink))]">{room.settings.infiltrators_count}</span>
                          <button
                            onClick={() => {
                              const currentInf = room.settings.infiltrators_count || 0;
                              const currentImp = room.settings.impostor_count || 0;
                              // Constraint: Agents >= Infiltrators
                              // Total - (Inf + 1) - Imp >= (Inf + 1)
                              if (room.players.length - currentImp >= 2 * currentInf + 2) {
                                updateSettings("infiltrators_count", currentInf + 1);
                              } else {
                                toast.error("No puede haber más infiltrados que agentes");
                              }
                            }}
                            className="w-8 h-8 rounded-full bg-[hsl(var(--hot-pink))]/10 text-[hsl(var(--hot-pink))] flex items-center justify-center hover:bg-[hsl(var(--hot-pink))]/20 active:scale-90 transition-all font-bold border border-[hsl(var(--hot-pink))]/20"
                          >+</button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[10px] uppercase font-bold text-[hsl(var(--text-muted))]">Impostor</Label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateSettings("impostor_count", 0)}
                            className={`flex-1 py-1 rounded border transition-all font-bold text-xs ${room.settings.impostor_count === 0 ? 'bg-[hsl(var(--electric-cyan))] text-black border-[hsl(var(--electric-cyan))]' : 'bg-transparent text-[hsl(var(--electric-cyan))] border-[hsl(var(--electric-cyan))]/30'}`}
                          >0</button>
                          <button
                            onClick={() => {
                              const currentInf = room.settings.infiltrators_count || 0;
                              if (room.players.length - currentInf - 1 >= currentInf) {
                                updateSettings("impostor_count", 1);
                              } else {
                                toast.error("No hay suficientes agentes para añadir un impostor");
                              }
                            }}
                            className={`flex-1 py-1 rounded border transition-all font-bold text-xs ${room.settings.impostor_count === 1 ? 'bg-[hsl(var(--electric-cyan))] text-black border-[hsl(var(--electric-cyan))]' : 'bg-transparent text-[hsl(var(--electric-cyan))] border-[hsl(var(--electric-cyan))]/30'}`}
                          >1</button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-[hsl(var(--smoke))] pt-2 pb-2 mt-4" />

                  {/* --- SECCIÓN 3: ACCESO --- */}
                  <div className="space-y-3">
                    <h4 className="font-heading text-sm text-[hsl(var(--text-muted))] uppercase tracking-wider mb-2">Acceso a la Sala</h4>

                    <div className="space-y-2">
                      <Label className="font-heading text-xs">Contraseña (opcional)</Label>
                      <Input
                        type="text"
                        placeholder="Sin contraseña"
                        value={room.settings?.password || ""}
                        onChange={(e) => updateSettings("password", e.target.value)}
                        className="bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="font-heading text-xs">Aprobar Jugadores</Label>
                      <Switch checked={room.settings?.require_approval ?? false} onCheckedChange={(c) => updateSettings("require_approval", c)} />
                    </div>
                  </div>
                </div>

                <div className="border-t border-[hsl(var(--smoke))] pt-4" />
              </>
            ) : (
              <p className="text-sm text-center text-[hsl(var(--text-muted))]">Solo el host puede cambiar la configuración.</p>
            )}

            {room.players.length >= 3 && (
              <div className="space-y-3">
                <h4 className="font-heading text-sm text-[hsl(var(--text-muted))] uppercase tracking-wider">Distribución de Roles</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><span className="text-sm">Agentes</span><Badge className="bg-[hsl(var(--cyber-yellow))]/20 text-[hsl(var(--cyber-yellow))]">{distribution.agents}</Badge></div>
                  <div className="flex items-center justify-between"><span className="text-sm">Infiltrados</span><Badge className="bg-[hsl(var(--hot-pink))]/20 text-[hsl(var(--hot-pink))]">{distribution.infiltrators}</Badge></div>
                  {distribution.impostor > 0 && <div className="flex items-center justify-between"><span className="text-sm">Impostor</span><Badge className="bg-[hsl(var(--electric-cyan))]/20 text-[hsl(var(--electric-cyan))]">{distribution.impostor}</Badge></div>}
                </div>
              </div>
            )}

            {isHost && (
              <Button onClick={startGame} disabled={!room || room.players.length < 3} className="w-full bg-[hsl(var(--cyber-yellow))] text-[hsl(240,10%,8%)] hover:bg-[hsl(var(--cyber-yellow))]/90 font-heading font-semibold text-lg py-6 shadow-[0_0_30px_rgba(255,220,90,0.4)]">
                {room.players.length < 3 ? "SE NECESITAN 3+ JUGADORES" : "INICIAR PARTIDA"}
              </Button>
            )}

            {!isHost && (
              <div className="text-center text-sm text-[hsl(var(--text-muted))] font-heading">
                Esperando a que el host inicie la partida...
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showRenameModal} onOpenChange={setShowRenameModal}>
        <DialogContent className="bg-[hsl(var(--deep-slate))] border-[hsl(var(--smoke))] text-white sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-[hsl(var(--cyber-yellow))]">Cambiar Nombre</DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-muted))]">
              Elige cómo quieres que te vean los demás jugadores.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              id="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="col-span-3 bg-[hsl(var(--charcoal))] border-[hsl(var(--smoke))]"
              placeholder="Nuevo nombre..."
              maxLength={15}
            />
          </div>
          <DialogFooter>
            <Button onClick={handleRename} className="bg-[hsl(var(--electric-cyan))] text-black hover:bg-[hsl(var(--electric-cyan))]/90 font-bold">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
